import React, { useEffect } from 'react';
import {
    CountLoadsByJobProppant,
    GetFullJobProfile_getJobProfile_pumpScheduleSummary as PumpScheduleSummaryData,
    GetFullJobProfile_getJobProfile_pumpScheduleSummary_proppants as ProppantData,
    GetAllProppants_getAllProppants as Proppant,
    GetLofaSchedule,
    GetLofaSchedule_getLofaSchedule as LOFAScheduleSummaryData,
} from '../../../../../types/schemaTypes';
import { PumpScheduleSummaryObject } from '../../../../../store/types/PumpScheduleEditState';
import { useStoreContext } from '../../../../App/StoreProvider';
import { useQuery, useLazyQuery } from '@apollo/client/react';
import * as PumpScheduleEditActions from '../../../../../store/actions/pumpScheduleEdit';
import { Textfield } from '../../../../_common/Textfield/Textfield';
import { GET_LOFA_SCHEDULE, COUNT_LOADS_BY_JOB_PROPPANT } from '../../../../../graphql/client/queries';
import SearchableDropdown from '../../../../_common/SearchableDropdown/SearchableDropdown';
import { InputTitleEnum } from '../../../../_common/InputTitleEnum/InputTitleEnum';
import { EditActions } from '../../../../../components/_common/EditLoadErrors/EditLoadErrors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';

library.add(fas);

const PROPPANT_TYPE_ERROR_MESSAGE = 'Selected proppant has already been added for this well.';

interface Suggestion {
    id: string | number;
    name: string;
}

interface ProppantRowProps {
    wellhead: PumpScheduleSummaryData;
    proppant: ProppantData;
    proppants: (Proppant | null)[] | null;
    isDeleteEnabled: boolean;
    jobId: number;
    isNewRow: boolean;
}

const ProppantRow = ({
    wellhead,
    proppant,
    proppants,
    isDeleteEnabled,
    jobId,
    isNewRow,
}: ProppantRowProps): React.ReactElement => {
    let proppantList: Suggestion[] = [];
    const lofaScheduleSummaryList: LOFAScheduleSummaryData[] = [];
    let lofaScheduleJobSummary;
    let lofaNameForProppant: any;
    const [{ pumpScheduleEdit }, dispatch] = useStoreContext();
    const key = `${wellhead.id}${!!proppant.id ? ':' + proppant.id : ''}`;
    const {
        proppantTypeError,
        proppantLoadsCountError,
        proppantTotalVolumeExceedError,
        proppantTotalVolumeError,
        proppantVolumePerBoxError,
    } = pumpScheduleEdit.fieldErrors[key] || {
        proppantTypeError: '',
        proppantLoadsCountError: '',
        proppantTotalVolumeExceedError: '',
        proppantTotalVolumeError: '',
        proppantVolumePerBoxError: '',
    };
    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    const [getLoadsOfJobProppant, { data: countLoadsData, loading: countLoadsLoading }] = useLazyQuery<CountLoadsByJobProppant>(
        COUNT_LOADS_BY_JOB_PROPPANT,
        { fetchPolicy: 'network-only' },
    );

    const { data: lofaData } = useQuery<GetLofaSchedule>(GET_LOFA_SCHEDULE, { variables: { jobId: jobId } });
    if (lofaData && lofaData.getLofaSchedule && lofaData.getLofaSchedule.length !== 0) {
        lofaData.getLofaSchedule.forEach((element: LOFAScheduleSummaryData | null): void => {
            if (element !== null) {
                lofaScheduleSummaryList.push(element);
            }
        });
    }

    useEffect(() => {
        if (countLoadsData && countLoadsData.countLoadsByJobProppant) {
            if (countLoadsData.countLoadsByJobProppant.count > 0) {
                dispatch(
                    PumpScheduleEditActions.setFieldError(
                        'proppantLoadsCountError',
                        EditActions.PROPPANT_LOADS_COUNT_ERROR_MESSAGE,
                        wellhead.id,
                        proppant.id,
                    ),
                );
            } else {
                dispatch(PumpScheduleEditActions.deleteProppant(wellhead, proppant));
            }
        }
    }, [countLoadsData, countLoadsLoading]);

    if (lofaScheduleSummaryList && lofaScheduleSummaryList.length > 0) {
        lofaScheduleJobSummary =
            lofaScheduleSummaryList &&
            lofaScheduleSummaryList
                .filter(it => (it && it.proppantId === proppant.proppantId) || 0)
                .map(it => it && it.jobSummaries)[0];
    }

    if (lofaScheduleJobSummary && lofaScheduleJobSummary.length > 0) {
        lofaNameForProppant = lofaScheduleJobSummary && lofaScheduleJobSummary.map(it => it && it.lofaName !== '')[0];
    }

    if (proppants) {
        proppantList = proppants
            .filter(proppant => proppant != null)
            .map(proppant => ({
                id: proppant && proppant.proppantId ? proppant.proppantId : 0,
                name: proppant && proppant.proppantName ? proppant.proppantName : '',
            }));
    }

    const checkAndUpdateTotalVolume = (proppantId: any, newPumpScheduleTotalVolume: any, isDelete: boolean): boolean => {
        if (isNewRow && isDelete) {
            dispatch(PumpScheduleEditActions.deleteProppant(wellhead, proppant));
            return true;
        }
        let wellHeadsFilteredByProppantId = 0;
        pumpScheduleEdit.currentValues &&
            pumpScheduleEdit.currentValues.map((it: PumpScheduleSummaryObject) => {
                if (it && it.proppants) {
                    const filteredList = it.proppants.filter(its => its && its.proppantId === proppantId);
                    filteredList.map(item => (wellHeadsFilteredByProppantId += (item && item.totalVolume) || 0));
                }
            });
        let currentWellHeadTotalVolume = 0;
        if (wellhead && wellhead.proppants && wellhead.proppants.length > 0) {
            wellhead.proppants.forEach((item: any) => {
                if (proppantId === item.proppantId) {
                    currentWellHeadTotalVolume = item.totalVolume;
                }
            });
        }
        let deleteHandled = false;
        let immediateDelete = false;
        if (lofaScheduleSummaryList && lofaScheduleSummaryList.length > 0) {
            lofaScheduleSummaryList.map(lofa => {
                if (isNewRow && isDelete) {
                    dispatch(PumpScheduleEditActions.deleteProppant(wellhead, proppant));
                    immediateDelete = true;
                    return;
                }
                if (lofa.proppantId === proppantId) {
                    let lowerTotalVolume = 0;
                    lofa &&
                        lofa.jobSummaries &&
                        lofa.jobSummaries.map(js => (lowerTotalVolume += (js && js.lofaVolume) || 0));

                    if (isDelete) {
                        if (
                            Math.sign(wellHeadsFilteredByProppantId - newPumpScheduleTotalVolume - lowerTotalVolume) < 0
                        ) {
                            dispatch(
                                PumpScheduleEditActions.setFieldError(
                                    'proppantTotalVolumeExceedError',
                                    EditActions.PROPPANT_TOTAL_VOLUME_DELETE_ERROR_MESSAGE,
                                    wellhead.id,
                                    proppant.id,
                                ),
                            );
                        } else {
                            dispatch(
                                PumpScheduleEditActions.updateProppant(
                                    wellhead,
                                    proppant,
                                    InputTitleEnum.TOTAL_VOLUME,
                                    0,
                                ),
                            );
                            deleteHandled = true;
                            getLoadsOfJobProppant({
                                variables: {
                                    input: {
                                        jobId: jobId,
                                        proppantId: proppant.proppantId,
                                    },
                                },
                            });
                        }
                    } else if (!isDelete) {
                        if (
                            !(
                                newPumpScheduleTotalVolume &&
                                +newPumpScheduleTotalVolume > 0 &&
                                +newPumpScheduleTotalVolume < 999999999
                            )
                        ) {
                            dispatch(
                                PumpScheduleEditActions.setFieldError(
                                    'proppantTotalVolumeError',
                                    EditActions.TOTAL_VOLUME_ERROR,
                                    wellhead.id,
                                    proppant.id,
                                ),
                            );
                        } else if (
                            Math.sign(
                                wellHeadsFilteredByProppantId -
                                    (currentWellHeadTotalVolume - newPumpScheduleTotalVolume) -
                                    lowerTotalVolume,
                            ) < 0
                        ) {
                            dispatch(
                                PumpScheduleEditActions.updateProppant(
                                    wellhead,
                                    proppant,
                                    InputTitleEnum.TOTAL_VOLUME,
                                    parseInt(newPumpScheduleTotalVolume),
                                ),
                            );
                            dispatch(
                                PumpScheduleEditActions.setFieldError(
                                    'proppantTotalVolumeError',
                                    '',
                                    wellhead.id,
                                    proppant.id,
                                ),
                            );
                            dispatch(
                                PumpScheduleEditActions.setFieldError(
                                    'proppantTotalVolumeExceedError',
                                    EditActions.PROPPANT_TOTAL_VOLUME_EXCEED_ERROR_MESSAGE,
                                    wellhead.id,
                                    proppant.id,
                                ),
                            );
                        } else {
                            dispatch(
                                PumpScheduleEditActions.updateProppant(
                                    wellhead,
                                    proppant,
                                    InputTitleEnum.TOTAL_VOLUME,
                                    parseInt(newPumpScheduleTotalVolume),
                                ),
                            );
                            dispatch(
                                PumpScheduleEditActions.setFieldError(
                                    'proppantTotalVolumeExceedError',
                                    '',
                                    wellhead.id,
                                    proppant.id,
                                ),
                            );
                            dispatch(
                                PumpScheduleEditActions.setFieldError(
                                    'proppantTotalVolumeError',
                                    '',
                                    wellhead.id,
                                    proppant.id,
                                ),
                            );
                        }
                    }
                } else if (isNewRow) {
                    dispatch(
                        PumpScheduleEditActions.updateProppant(
                            wellhead,
                            proppant,
                            InputTitleEnum.TOTAL_VOLUME,
                            parseInt(newPumpScheduleTotalVolume),
                        ),
                    );
                }
            });
            if (immediateDelete) return true;
            if (isDelete && !deleteHandled) {
                getLoadsOfJobProppant({
                    variables: {
                        input: {
                            jobId: jobId,
                            proppantId: proppant.proppantId,
                        },
                    },
                });
            }
            return false;
        } else {
            if (isDelete) {
                getLoadsOfJobProppant({
                    variables: {
                        input: {
                            jobId: jobId,
                            proppantId: proppant.proppantId,
                        },
                    },
                });
            } else {
                dispatch(
                    PumpScheduleEditActions.updateProppant(
                        wellhead,
                        proppant,
                        InputTitleEnum.TOTAL_VOLUME,
                        parseInt(newPumpScheduleTotalVolume),
                    ),
                );
            }
            return false;
        }
    };

    const updateAndValidateVolumePerBox = (value: any): void => {
        if (value && +value > 999 && +value < 56001) {
            dispatch(PumpScheduleEditActions.setFieldError('proppantVolumePerBoxError', '', wellhead.id, proppant.id));
            dispatch(
                PumpScheduleEditActions.updateProppant(
                    wellhead,
                    proppant,
                    InputTitleEnum.VOLUME_PER_BOX,
                    parseInt(value),
                ),
            );
        } else {
            dispatch(
                PumpScheduleEditActions.setFieldError(
                    'proppantVolumePerBoxError',
                    EditActions.VOLUME_PER_BOX_ERROR,
                    wellhead.id,
                    proppant.id,
                ),
            );
        }
    };

    const checkIsDisabled = (): boolean => {
        if (lofaNameForProppant && lofaNameForProppant !== null && !isNewRow) {
            return true;
        }
        return false;
    };

    const deleteProppantRow = (): void => {
        const saveTotalVolume = proppant.totalVolume;
        const didImmediateDelete = checkAndUpdateTotalVolume(proppant.proppantId, proppant.totalVolume, true);
        if (!didImmediateDelete) {
            dispatch(
                PumpScheduleEditActions.updateProppant(wellhead, proppant, InputTitleEnum.TOTAL_VOLUME, saveTotalVolume),
            );
        }
    };

    const updateProppantValue = (eventKey: string): void => {
        let existingProppantType = false;
        dispatch(PumpScheduleEditActions.setFieldError('proppantTypeError', '', wellhead.id, proppant.id));
        if (wellhead && wellhead.proppants) {
            wellhead.proppants.forEach((item: any) => {
                if (item.proppantId === +eventKey) {
                    existingProppantType = true;
                    dispatch(
                        PumpScheduleEditActions.setFieldError(
                            'proppantTypeError',
                            PROPPANT_TYPE_ERROR_MESSAGE,
                            wellhead.id,
                            proppant.id,
                        ),
                    );
                }
            });
        }

        if (!existingProppantType) {
            const proppantName =
                proppants &&
                proppants
                    .filter(item => eventKey != null && item && item.proppantId === +eventKey)
                    .map(item => item && item.proppantName)[0];

            const proppantValue = {
                id: +eventKey,
                value: proppantName,
            };

            dispatch(PumpScheduleEditActions.updateProppantValue(wellhead, proppant, proppantValue));
        }
    };

    const handleAutoCompleteChange = (eventKey: string | null): void => {
        if (eventKey) {
            updateProppantValue(eventKey);

            if (+eventKey === 0) {
                dispatch(
                    PumpScheduleEditActions.setFieldError(
                        'proppantTypeError',
                        'Please enter a valid proppant name',
                        wellhead.id,
                        proppant.id,
                    ),
                );
            }
        }
    };

    return (
        <div className="PumpScheduleEdit-ProppantMainContainer">
            <div className="PumpScheduleEdit-Container">
                {isDeleteEnabled && (
                    <i className="material-icons PumpScheduleEdit-DeleteIcon" onClick={deleteProppantRow}>
                        highlight_off
                    </i>
                )}
                <div className="PumpSchedule-Autocomplete-Container">
                    <SearchableDropdown
                        autoCompleteEnabled={true}
                        disabled={checkIsDisabled()}
                        dropdownOptions={proppantList}
                        errorMessage={proppantTypeError || ''}
                        hasError={!!proppantTypeError}
                        id="proppantTitle"
                        label="PROPPANT NAME"
                        onSelect={(eventKey: string | null) => {
                            handleAutoCompleteChange(eventKey);
                        }}
                        required
                        value={proppant.proppantName}
                        endIcon={searchIcon}
                    />
                </div>

                <div className="PumpSchedule-EditDetail">
                    <Textfield
                        label="TOTAL VOLUME"
                        onChange={(value): void => {
                            // TODO: Need validation here for all, query LofaSchedule and add up each proppant total
                            // this field cannot be less than the sum of each proppant/Lofa
                            checkAndUpdateTotalVolume(proppant.proppantId || 0, +value, false);
                        }}
                        value={proppant.totalVolume}
                        inputTitle={InputTitleEnum.TOTAL_VOLUME}
                        error={!!proppantTotalVolumeExceedError || !!proppantTotalVolumeError}
                        helperText={proppantTotalVolumeExceedError || proppantTotalVolumeError || ''}
                        thousandSeparator
                        maxLength={11}
                    />
                </div>
                <div className="PumpSchedule-EditDetail">
                    <Textfield
                        label="PLANNED VOL/BOX"
                        onChange={(value): void => {
                            updateAndValidateVolumePerBox(+value);
                        }}
                        value={proppant.volumePerBox}
                        inputTitle={InputTitleEnum.VOLUME_PER_BOX}
                        error={!!proppantVolumePerBoxError}
                        helperText={proppantVolumePerBoxError || ''}
                        thousandSeparator
                        maxLength={6}
                    />
                </div>
            </div>
            <div className="PumpSchedule-ProppantLoadsError">{proppantLoadsCountError}</div>
        </div>
    );
};

export default ProppantRow;
